import { ActivityIndicator, StyleSheet, Text, View, ViewProps } from 'react-native'

import { tokens } from '../../theme/tokens'

export interface LoaderProps extends ViewProps {
  /** Optional status message rendered under the spinner. Pass a translated string. */
  message?: string
  size?: 'small' | 'large'
  color?: string
}

/**
 * Shared loading spinner.
 *
 * Consolidates CWA's `Loader` and `ReportIssue/LoadingSpinner` and CMA's
 * `AppLoading` spinner. Fills and centers within its container; sizing and
 * placement (full-screen, overlay, inline) belong to the caller via `style`.
 */
export const Loader = ({
  message,
  size = 'large',
  color = tokens.colors.accent[300],
  style,
  ...passthroughProps
}: LoaderProps) => {
  return (
    // On web (react-native-web) ActivityIndicator already exposes
    // role="progressbar"; adding it here too would duplicate the role. Native
    // RN exposes no role for it — acceptable for a decorative spinner.
    <View {...passthroughProps} style={[styles.container, style]}>
      <ActivityIndicator size={size} color={color} />
      {message ? <Text style={styles.message}>{message}</Text> : null}
    </View>
  )
}

Loader.displayName = 'Loader'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  message: {
    fontSize: 14,
    // secondaryGrey (#858581) is the ACCEPTED token carrying the value the
    // deleted CWA LoadingSpinner rendered; tokens.brand.darkGrey is a PROPOSED
    // collision pending CBP-3947 (see theme/DECISIONS.md) and must not ship.
    color: tokens.colors.brand.secondaryGrey,
  },
})
